import { Navigate, type RouteDefinition, useLocation } from "@solidjs/router";
import { Show, lazy } from "solid-js";
import type { JSX } from "solid-js";

import { AppShell } from "@/components/layout/AppShell";
import { useAuth } from "@/features/auth/AuthProvider";
import { canAccessPath, firstAllowedPath } from "@/features/auth/capabilities";
import { LoginPage } from "@/features/auth/LoginPage";
import { ProtectedRoute } from "@/features/auth/ProtectedRoute";
import { isLearnerUser } from "@/features/auth/role";
import { SignupPage } from "@/features/onboarding/SignupPage";

const TrainersPage = lazy(async () => ({ default: (await import("@/features/trainers/TrainersPage")).TrainersPage }));

const FormationsListPage = lazy(async () => ({
  default: (await import("@/features/catalog/formations/FormationsListPage")).FormationsListPage,
}));
const FormationDetailPage = lazy(async () => ({
  default: (await import("@/features/catalog/formations/FormationDetailPage")).FormationDetailPage,
}));
const SessionsListPage = lazy(async () => ({
  default: (await import("@/features/catalog/sessions/SessionsListPage")).SessionsListPage,
}));
const SessionDetailPage = lazy(async () => ({
  default: (await import("@/features/catalog/sessions/SessionDetailPage")).SessionDetailPage,
}));
const HealthCheck = lazy(async () => ({
  default: (await import("@/features/health/HealthCheck")).HealthCheck,
}));
const PublicCatalogPage = lazy(async () => ({
  default: (await import("@/features/catalog/public/PublicCatalogPage")).PublicCatalogPage,
}));
const LearnersListPage = lazy(async () => ({
  default: (await import("@/features/enrollments/learners/LearnersListPage")).LearnersListPage,
}));
const LearnerDetailPage = lazy(async () => ({
  default: (await import("@/features/enrollments/learners/LearnerDetailPage")).LearnerDetailPage,
}));
const EmployersListPage = lazy(async () => ({
  default: (await import("@/features/enrollments/employers/EmployersListPage")).EmployersListPage,
}));
const EmployerDetailPage = lazy(async () => ({
  default: (await import("@/features/enrollments/employers/EmployerDetailPage")).EmployerDetailPage,
}));
const EnrollmentsListPage = lazy(async () => ({
  default: (await import("@/features/enrollments/enrollments/EnrollmentsListPage"))
    .EnrollmentsListPage,
}));
const EnrollmentDetailPage = lazy(async () => ({
  default: (await import("@/features/enrollments/enrollments/EnrollmentDetailPage"))
    .EnrollmentDetailPage,
}));
const AttendanceSheetPage = lazy(async () => ({
  default: (await import("@/features/attendance/AttendanceSheetPage")).AttendanceSheetPage,
}));
const InvoicesListPage = lazy(async () => ({
  default: (await import("@/features/billing/InvoicesListPage")).InvoicesListPage,
}));
const InvoiceDetailPage = lazy(async () => ({
  default: (await import("@/features/billing/InvoiceDetailPage")).InvoiceDetailPage,
}));
const TvaReportPage = lazy(async () => ({
  default: (await import("@/features/billing/TvaReportPage")).TvaReportPage,
}));
const BudgetReportPage = lazy(async () => ({
  default: (await import("@/features/billing/BudgetReportPage")).BudgetReportPage,
}));
const RoomScanPage = lazy(async () => ({
  default: (await import("@/features/attendance-qr/RoomScanPage")).RoomScanPage,
}));
const DashboardPage = lazy(async () => ({
  default: (await import("@/features/dashboard/DashboardPage")).DashboardPage,
}));
const RoomsPage = lazy(async () => ({
  default: (await import("@/features/infrastructure/RoomsPage")).RoomsPage,
}));
const PartnersPage = lazy(async () => ({
  default: (await import("@/features/infrastructure/PartnersPage")).PartnersPage,
}));
const CalendarPage = lazy(async () => ({
  default: (await import("@/features/calendar/CalendarPage")).CalendarPage,
}));
const OpportunitiesPage = lazy(async () => ({
  default: (await import("@/features/opportunities/OpportunitiesPage")).OpportunitiesPage,
}));
const EduquaPage = lazy(async () => ({
  default: (await import("@/features/eduqua/EduquaPage")).EduquaPage,
}));
const MePage = lazy(async () => ({
  default: (await import("@/features/me/MePage")).MePage,
}));
const VerifyPage = lazy(async () => ({
  default: (await import("@/features/verify/VerifyPage")).VerifyPage,
}));
const SettingsPage = lazy(async () => ({
  default: (await import("@/features/settings/SettingsPage")).SettingsPage,
}));
const AccessWorkshopPage = lazy(async () => ({
  default: (await import("@/features/access/AccessWorkshopPage")).AccessWorkshopPage,
}));
const ImportPage = lazy(async () => ({
  default: (await import("@/features/import/ImportPage")).ImportPage,
}));
const PlatformSchoolsPage = lazy(async () => ({
  default: (await import("@/features/platform/PlatformSchoolsPage")).PlatformSchoolsPage,
}));

function Shell(props: { children: JSX.Element }) {
  const auth = useAuth();
  const location = useLocation();
  const allowed = () =>
    canAccessPath(location.pathname, auth.role(), auth.capabilities(), auth.platformAdmin());
  return (
    <ProtectedRoute>
      {/* Un apprenant connecté n'a pas accès au back-office staff : on le
          renvoie vers son espace /me (couvre le login form ET le lien magique). */}
      <Show when={!isLearnerUser(auth.user())} fallback={<Navigate href="/me" />}>
        <Show
          when={!auth.meLoading()}
          fallback={
            <div class="flex min-h-screen items-center justify-center text-sm text-[var(--fg-muted)]">
              Chargement des accès…
            </div>
          }
        >
          <Show
            when={allowed()}
            fallback={<Navigate href={firstAllowedPath(auth.role(), auth.capabilities(), auth.platformAdmin())} />}
          >
            <AppShell>{props.children}</AppShell>
          </Show>
        </Show>
      </Show>
    </ProtectedRoute>
  );
}

export const routes: RouteDefinition[] = [
  { path: "/login", component: LoginPage },
  { path: "/signup", component: SignupPage },

  // Catalogue public — pas de Shell, pas d'auth.
  { path: "/public/c/:subdomain", component: PublicCatalogPage },

  // Émargement QR par salle — public, QR permanent posé à l'entrée de la salle.
  // Backend résoud le cours actif via room_id + horaire CH automatiquement.
  { path: "/room-scan/:token", component: RoomScanPage },

  // Vérification publique d'attestation — public, scan QR depuis PDF.
  { path: "/verify/:token", component: VerifyPage },

  // Espace apprenant — auth requise mais pas de Shell staff.
  {
    path: "/me",
    component: () => (
      <ProtectedRoute>
        <MePage />
      </ProtectedRoute>
    ),
  },

  // Back-office authentifié
  {
    path: "/",
    component: (props) => <Shell>{props.children}</Shell>,
    children: [
      { path: "/", component: DashboardPage },
      { path: "/dashboard", component: DashboardPage },
      { path: "/formations", component: FormationsListPage },
      { path: "/trainers", component: TrainersPage },
      { path: "/formations/:id", component: FormationDetailPage },
      { path: "/sessions", component: SessionsListPage },
      { path: "/calendar", component: CalendarPage },
      { path: "/opportunities", component: OpportunitiesPage },
      { path: "/sessions/:id", component: SessionDetailPage },
      { path: "/learners", component: LearnersListPage },
      { path: "/learners/:id", component: LearnerDetailPage },
      { path: "/employers", component: EmployersListPage },
      { path: "/employers/:id", component: EmployerDetailPage },
      { path: "/enrollments", component: EnrollmentsListPage },
      { path: "/enrollments/:id", component: EnrollmentDetailPage },
      { path: "/sessions/:id/attendance", component: AttendanceSheetPage },
      { path: "/invoices", component: InvoicesListPage },
      { path: "/invoices/:id", component: InvoiceDetailPage },
      { path: "/rooms", component: RoomsPage },
      { path: "/partners", component: PartnersPage },
      { path: "/reports/budget", component: BudgetReportPage },
      { path: "/reports/tva", component: TvaReportPage },
      { path: "/reports/eduqua", component: EduquaPage },
      { path: "/settings", component: SettingsPage },
      { path: "/settings/access", component: AccessWorkshopPage },
      { path: "/import", component: ImportPage },
      { path: "/health", component: HealthCheck },
      { path: "/platform/schools", component: PlatformSchoolsPage },
    ],
  },
];
